import React, { useState, useMemo, useEffect } from 'react';
import { get, isNumber, omit, pullAt } from 'lodash';
import PropTypes from 'prop-types';
import Button from '@tds/core-button';
import { Delete, IconButton, Edit } from '@tds/core-interactive-icon';
import Modal from '../../../design/shared/modal';
import {
  Td,
  Tr,
  Th,
  Table,
  TdImp,
  TrError,
  Tdtotal,
  TableContainer,
  CategoryTitle,
  TdCategoryTotal,
  customStyles,
  ModalFieldLabel,
  CustomField,
  ModalBottomContainer
} from './css';
import { formatCurrency } from '../../../utils/currencyFormatter';

const SelectionsGrid = ({
  schema,
  onChangeSelections,
  defaultPriceSelections,
  defaultCustomSelections,
  defaultNumberFields,
  defaultSelections,
  orderData,
  index: categoryIndex,
  extraData,
  errors
}) => {
  const [priceSelections, setPriceSelections] = useState({});
  const [selections, setSelections] = useState({});
  const [numberFields, setNumberFields] = useState({});
  const [customSelections, setCustomSelections] = useState({});

  const [isCustomModalVisible, setCustomModalVisible] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  const [modalFieldContent, setModalFieldContent] = useState({});
  const [modalData, setModalData] = useState(null);
  const [rowIndex, setRowIndex] = useState();

  useEffect(() => {
    setPriceSelections(defaultPriceSelections);
    setSelections(defaultSelections);
    setNumberFields(defaultNumberFields);
    setCustomSelections(defaultCustomSelections);
  }, [orderData]);

  const sectionTotals = useMemo(() => {
    const categoryTotalsObj = {};
    Object.keys(selections).forEach(selection => {
      const { totalCalculator } = schema.sections.find(
        section => section.id === selection
      );
      categoryTotalsObj[selection] = totalCalculator(
        priceSelections[selection],
        numberFields[selection],
        customSelections[selection],
        extraData
      );
    });

    Object.keys(customSelections).forEach(selection => {
      const { totalCalculator } = schema.sections.find(
        section => section.id === selection
      );
      categoryTotalsObj[selection] = totalCalculator(
        priceSelections[selection],
        numberFields[selection],
        customSelections[selection]
      );
    });

    return categoryTotalsObj;
  }, [priceSelections, selections, numberFields, customSelections, extraData]);

  const categoryTotals = useMemo(() => {
    return schema.categoryTotalCalculator(sectionTotals);
  }, [sectionTotals]);

  const onSelect = (event, options, sectionId) => {
    const selectedOption = options.find(
      option => option.value === event.target.value
    );
    setPriceSelections({
      ...priceSelections,
      [sectionId]: {
        ...priceSelections[sectionId],
        [event.target.id]: selectedOption.additionalData
      }
    });
    setSelections({
      ...selections,
      [sectionId]: {
        ...selections[sectionId],
        [event.target.id]: event.target.value
      }
    });
  };

  useEffect(() => {
    onChangeSelections(
      sectionTotals,
      categoryTotals,
      selections,
      customSelections,
      numberFields,
      priceSelections
    );
  }, [selections, customSelections, numberFields]);

  const onInputChange = (event, sectionId) => {
    // Check if the input is a non-negative number
    if (!event.target.value.includes('-')) {
      setNumberFields({
        ...numberFields,
        [sectionId]: {
          ...numberFields[sectionId],
          [event.target.id]: event.target.value
        }
      });
    }
    if (!event.target.value || event.target.value === '0') {
      if (selections[sectionId]) {
        setSelections(omit(selections, sectionId));
      }
      if (priceSelections[sectionId]) {
        setPriceSelections(omit(priceSelections, sectionId));
      }
    }
  };

  const onFixedValueInputChange = (
    event,
    fixedValues,
    sectionId,
    rowTotalConfig
  ) => {
    const result = {};

    rowTotalConfig.forEach(key => {
      if (Object.prototype.hasOwnProperty.call(fixedValues, key)) {
        result[key] = fixedValues[key] * event.target.value;
      }
    });
    setPriceSelections({
      ...priceSelections,
      [sectionId]: {
        ...priceSelections[sectionId],
        [event.target.id]: result
      }
    });

    setSelections({
      ...selections,
      [sectionId]: {
        ...selections[sectionId],
        [event.target.id]: event.target.value
      }
    });
  };

  const onCloseCustomModal = () => {
    setModalData(null);
    setCustomModalVisible(false);
  };

  const showCustomModal = () => {
    setCustomModalVisible(true);
  };

  const validateModalFields = (values, inputFields) => {
    const areEntriesValid = inputFields.every(
      inputField => values[inputField.id]
    );
    if (!areEntriesValid) {
      setModalErrorMessage('Please add values for the mandatory fields');
    } else {
      setModalErrorMessage('');
    }
    return areEntriesValid;
  };

  const onAddFields = (inputFields, sectionId) => {
    if (modalData && validateModalFields(modalData, inputFields)) {
      const updateData = [...customSelections[sectionId]];
       updateData[rowIndex] = modalData;
      setCustomSelections({
        ...customSelections,
        [sectionId]: [...updateData]
      });
      setModalFieldContent({});
      onCloseCustomModal();
      modalData(null);
    } else if (validateModalFields(modalFieldContent, inputFields)) {
      setCustomSelections({
        ...customSelections,
        [sectionId]: [
          ...(customSelections[sectionId] ? customSelections[sectionId] : []),
          modalFieldContent
        ]
      });
      setModalFieldContent({});
      onCloseCustomModal();
    }
  };

  const editItem = (row, sectionId) => {
    const data = customSelections[sectionId];
    setModalData(data[row]);
    setRowIndex(row);
    setCustomModalVisible(true);
  };

  const onRemoveFields = (idx, sectionId) => {
    const selectedServices = get(customSelections, `${sectionId}`, []);
    pullAt(selectedServices, idx);
    setCustomSelections({
      ...customSelections,
      [sectionId]: selectedServices
    });
  };

  const onDeleteCustomItem = (e, idx, sectionId) => {
    e.preventDefault();
    onRemoveFields(idx, sectionId, sectionId);
  };

  useEffect(() => {
    schema.sections.forEach(section => {
      if (section.items && section.items.length) {
        section.items.forEach(sectionItem => {
          if (
            isNumber(sectionItem.preselectOption) &&
            sectionItem.options &&
            sectionItem.options.length >= 1
          ) {
            const event = {
              target: {
                value: sectionItem.options[sectionItem.preselectOption].value,
                id: sectionItem.id
              }
            };
            onSelect(event, sectionItem.options, section.id);
          }
        });
      }
    });
  }, []);

  return (
    <TableContainer>
      {schema.categoryName && (
        <CategoryTitle>
          {!schema.isTitelVisible &&
            ` ${categoryIndex + 1}. ${schema.categoryName}`}
          <span style={{ display: 'block', fontSize: '0.8rem' }}>
            {schema.note}
          </span>
        </CategoryTitle>
      )}
      {schema.sections.map((sectionItem, sectionIndex) => {
        const sectionError = get(
          errors,
          `${schema.categoryId}.${sectionItem.id}`,
          false
        );
        return (
          <>
            {/* {sectionItem.title && <Tt>{sectionItem.title}</Tt>} */}
            <Table>
              {/* {schema.sections.some(section => section.numberField) && <Th />} */}
              {(!sectionItem.customInputs ||
                (customSelections[sectionItem.id] &&
                  customSelections[sectionItem.id].length > 0)) && (
                <>
                  <Th style={{ textAlign: 'left' }}>{sectionItem.title}</Th>
                  <Th style={{ width: '44%' }}></Th>
                  {/* <Th style={{ width: 80 }}>Cost</Th> */}
                  <Th style={{ width: 120 }}>Price</Th>
                  {sectionItem.customInputs && <Th colSpan={2}>Actions</Th>}
                </>
              )}

              {sectionError && (
                <TrError>
                  <Td colSpan={4} style={{ textAlign: 'left' }}>
                    {sectionError}
                  </Td>
                </TrError>
              )}
              {sectionItem.items &&
                sectionItem.items.map((item, index) => {
                  const areDropdownsDisabledForThisSections =
                    sectionItem.numberField &&
                    !+get(
                      numberFields,
                      `${sectionItem.id}.${sectionItem.numberField.id}`,
                      0
                    );
                  return (
                    <>
                      {index === 0 && sectionItem.numberField && (
                        <Tr>
                          <TdImp style={{ textAlign: 'left' }}>
                            {sectionItem.numberField.label}
                          </TdImp>
                          <Td
                            style={{
                              textAlign: 'left'
                            }}
                          >
                            {/* <div style={{ marginBottom: 6 }}>
                            {sectionItem.numberField.label}
                          </div> */}
                            <input
                              id={sectionItem.numberField.id}
                              name={sectionItem.numberField.id}
                              type="Number"
                              onWheel={e => e.target.blur()}
                              value={
                                (numberFields[sectionItem.id] &&
                                  numberFields[sectionItem.id][
                                    sectionItem.numberField.id
                                  ]) ||
                                ''
                              }
                              placeholder={sectionItem.numberField.label}
                              className="frg-input-field color-light"
                              style={{ width: '100%' }}
                              onChange={e => onInputChange(e, sectionItem.id)}
                            />
                          </Td>
                          <TdImp>-</TdImp>
                        </Tr>
                      )}
                      <Tr>
                        <TdImp style={{ textAlign: 'left' }}>
                          {item.label}
                          <span
                            style={{
                              display: 'block',
                              fontWeight: 'lighter',
                              fontStyle: 'italic',
                              fontSize: '13px'
                            }}
                          >
                            {item.note ? item.note : ''}
                          </span>
                        </TdImp>
                        <Td>
                          {item.options ? (
                            <select
                              style={{
                                backgroundColor: areDropdownsDisabledForThisSections
                                  ? '#f7f7f7'
                                  : 'unset'
                              }}
                              id={item.id}
                              onChange={e =>
                                onSelect(e, item.options, sectionItem.id)
                              }
                              disabled={areDropdownsDisabledForThisSections}
                              value={
                                (selections[sectionItem.id] &&
                                  selections[sectionItem.id][item.id]) ||
                                ''
                              }
                            >
                              <option disabled selected value="">
                                Select
                              </option>
                              {item.options.map(option => (
                                <option value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              id={item.id}
                              name={item.id}
                              type="Number"
                              onWheel={e => e.target.blur()}
                              value={
                                (selections[sectionItem.id] &&
                                  selections[sectionItem.id][item.id]) ||
                                ''
                              }
                              placeholder={item.label}
                              className="frg-input-field color-light"
                              style={{ width: '100%' }}
                              onChange={e =>
                                onFixedValueInputChange(
                                  e,
                                  item.fixedPrices,
                                  sectionItem.id,
                                  item.rowTotalConfig
                                )
                              }
                            />
                          )}
                        </Td>
                        {item.rowTotalConfig.map((configItem, idx) => {
                          if (idx !== 0) {
                            if (item.fixedPrices) {
                              return <Td>${item.fixedPrices[configItem]}</Td>;
                            }
                            return (
                              <Td>
                                $
                                {priceSelections[sectionItem.id] &&
                                priceSelections[sectionItem.id][item.id] &&
                                priceSelections[sectionItem.id][item.id][
                                  configItem
                                ]
                                  ? priceSelections[sectionItem.id][item.id][
                                      configItem
                                    ].toFixed(2)
                                  : '0.00'}
                              </Td>
                            );
                          }
                          return null; // Return null for the first item
                        })}
                      </Tr>
                    </>
                  );
                })}
              {sectionItem.customInputs && (
                <>
                  {/* TODO: Make this dynamic */}
                  {customSelections[sectionItem.id] &&
                    customSelections[sectionItem.id].map(
                      (customSelection, idx) => (
                        <Tr>
                          <Td style={{ textAlign: 'left' }} colSpan={2}>
                            {customSelection.serviceDescription}
                          </Td>
                          {/* <Td>${customSelection.serviceCost}</Td> */}
                          <Td>
                            {formatCurrency(customSelection.servicePrice)}
                          </Td>
                          {/* <Td> </Td> */}
                          <Td style={{ width: '90px' }}>
                            <IconButton
                              icon={Edit}
                              onClick={() => editItem(idx, sectionItem.id)}
                            />
                          </Td>
                          <Td style={{ width: '90px' }}>
                            <IconButton
                              icon={Delete}
                              onClick={e =>
                                onDeleteCustomItem(e, idx, sectionItem.id)
                              }
                            />
                          </Td>
                        </Tr>
                      )
                    )}
                  <Tr>
                    <Modal
                      open={isCustomModalVisible}
                      setOpen={onCloseCustomModal}
                      customStyles={customStyles}
                      header={
                        <div>
                          {sectionItem.customInputs.message && (
                            <span style={{ display: 'block' }}>
                              {modalData
                                ? 'Update Additional Service'
                                : sectionItem.customInputs.message}
                            </span>
                          )}
                        </div>
                      }
                    >
                      <div>
                        {sectionItem.customInputs.inputFields.map(
                          ({ type, id, label }) => {
                            return (
                              <CustomField>
                                <ModalFieldLabel>{label}*</ModalFieldLabel>
                                <input
                                  type={type}
                                  id={id}
                                  name={id}
                                  className="frg-input-field color-light"
                                  min={0}
                                  style={{ width: '100%' }}
                                  // value={modalFieldContent[id] || ''}
                                  value={
                                    modalData
                                      ? modalData[id]
                                      : modalFieldContent[id] || ''
                                  }
                                  onChange={e => {
                                    setModalFieldContent({
                                      ...modalFieldContent,
                                      [e.target.id]: e.target.value
                                    });
                                    modalData &&
                                      setModalData({
                                        ...modalData,
                                        [id]: e.target.value
                                      });
                                  }}
                                />
                              </CustomField>
                            );
                          }
                        )}
                        {modalErrorMessage}
                        <ModalBottomContainer>
                          <Button
                            onClick={() =>
                              onAddFields(
                                sectionItem.customInputs.inputFields,
                                sectionItem.id
                              )
                            }
                            variant="secondary"
                          >
                            {sectionItem.customInputs.buttonTextAdd}
                          </Button>
                        </ModalBottomContainer>
                      </div>
                    </Modal>
                  </Tr>
                </>
              )}
              {(!sectionItem.customInputs ||
                (customSelections[sectionItem.id] &&
                  customSelections[sectionItem.id].length > 0)) && (
                <Tr>
                  {/* {schema.sections.some(section => section.numberField) && (
                <Tdtotal />
              )} */}
                  <Tdtotal />
                  <Tdtotal>Total</Tdtotal>
                  {sectionTotals[sectionItem.id] &&
                    sectionTotals[sectionItem.id]
                      .slice(1)
                      .map(total => (
                        <Tdtotal>{formatCurrency(total) || 0}</Tdtotal>
                      ))}
                  {!sectionTotals[sectionItem.id] &&
                    sectionItem.defaultTotals
                      .slice(1)
                      .map(defaultTotal => (
                        <Tdtotal>{formatCurrency(defaultTotal)}</Tdtotal>
                      ))}
                </Tr>
              )}
              {schema.isFinalTotalVisible &&
                sectionIndex === schema.sections.length - 1 && (
                  <Tr>
                    {/* {schema.sections.some(section => section.numberField) && (
                  <TdCategoryTotal />
                )} */}
                    <TdCategoryTotal />
                    <TdCategoryTotal>
                      {schema.categoryName} Total
                    </TdCategoryTotal>
                    {categoryTotals.slice(1).map(total => (
                      <TdCategoryTotal>
                        {formatCurrency(total) || 0.0}
                      </TdCategoryTotal>
                    ))}
                    {sectionItem.customInputs && (
                      <TdCategoryTotal colSpan={2} />
                    )}
                  </Tr>
                )}
            </Table>
            {sectionItem.customInputs && (
              <div style={{ margin: '10px' }}>
                <Button onClick={showCustomModal}>
                  {sectionItem.customInputs.buttonText}
                </Button>
              </div>
            )}
          </>
        );
      })}
    </TableContainer>
  );
};

const optionShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  additionalData: PropTypes.shape({
    cost: PropTypes.number.isRequired,
    price: PropTypes.number.isRequired
  }).isRequired
});

const schemaShape = PropTypes.shape({
  categoryName: PropTypes.string,
  categoryId: PropTypes.string,
  categoryTotalCalculator: PropTypes.func.isRequired,
  note: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      id: PropTypes.string.isRequired,
      defaultTotals: PropTypes.arrayOf(PropTypes.number),
      numberField: PropTypes.shape({
        label: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        defaultValue: PropTypes.number
      }),
      items: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          id: PropTypes.string.isRequired,
          options: PropTypes.arrayOf(optionShape).isRequired,
          rowTotalConfig: PropTypes.arrayOf(PropTypes.string).isRequired
        })
      ).isRequired,
      totalCalculator: PropTypes.func.isRequired
    })
  )
});

// Usage in a component
SelectionsGrid.propTypes = {
  schema: schemaShape.isRequired,
  onChangeSelections: PropTypes.func.isRequired,
  defaultPriceSelections: PropTypes.arrayOf({}).isRequired,
  defaultSelections: PropTypes.arrayOf({}).isRequired,
  defaultNumberFields: PropTypes.arrayOf({}).isRequired,
  defaultCustomSelections: PropTypes.arrayOf({}).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  orderData: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  errors: PropTypes.shape({}).isRequired,
  extraData: PropTypes.shape({}).isRequired
};

export default SelectionsGrid;
